import { createContext, useContext, useRef } from 'react';
import { useSpeechSynthesis as useSpeechSynthesisKit } from 'react-speech-kit';
import { useGeneralState } from './general-state';

const SpeechSynthesisContext = createContext({
  speak: (text) => {},
});

const findVoice = (voices, preferredVoice) => {
  if (!voices) return null;

  let selectedVoiceValue;
  let ukVoices;
  let ukVoice;

  ukVoices = voices.filter(
    (v, index) => v.lang.includes('en-GB') || v.lang.includes('en_GB')
  );

  if (preferredVoice === 'MALE') {
    selectedVoiceValue = voices.find(
      (v, index) =>
        v.voiceURI.includes('Daniel') ||
        v.name.includes('Daniel') ||
        v.voiceURI.includes('daniel')
    );
    if (!selectedVoiceValue) {
      ukVoice = ukVoices?.find((v) => v?.voiceURI?.includes('Male'));
    }
  } else {
    // FEMALE
    selectedVoiceValue = voices.find(
      (v, index) =>
        v.voiceURI.includes('Martha') ||
        v.name.includes('Martha') ||
        v.voiceURI.includes('martha')
    );
    if (!selectedVoiceValue) {
      ukVoice = ukVoices?.find((v) => v?.voiceURI?.includes('Female'));
    }
  }

  selectedVoiceValue =
    selectedVoiceValue || ukVoice || ukVoices?.[0] || voices[0];

  return selectedVoiceValue;
};

const isIOS = () => {
  // Check for iOS user agent patterns
  const userAgent = navigator.userAgent;
  const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);

  // Check for iPad Pro running iOS 13+ which might report as Mac
  const isMacLikeDevice =
    userAgent.includes('Mac') && navigator.maxTouchPoints > 1;

  return isIOSDevice || isMacLikeDevice;
};

const SpeechSynthesisProvider = (props) => {
  const onEndFn = useRef(() => {});
  const intervalRef = useRef(null);
  const { state } = useGeneralState();
  const {
    cancel,
    speak,
    voices,
    speaking,
    //  speaking, supported, pause, resume
  } = useSpeechSynthesisKit({
    onEnd: () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (typeof onEndFn.current === 'function') {
        onEndFn.current();
      }
    },
  });
  let selectedVoiceValue = findVoice(voices, state.preferredVoice);

  const _speak = (text, onEnd = () => {}) => {
    onEndFn.current = onEnd;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (speaking) {
      cancel();
    }

    const cleanedText = text.replace(/(\d+)\s*-\s+(?!\d)/g, '$1 ');

    const isIOSDevice = isIOS();

    // Cancel any existing speech before starting new one
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }

    setTimeout(
      () => {
        speak({
          text: cleanedText,
          voice: selectedVoiceValue,
          rate: isIOSDevice ? 0.8 : 1, // Slower rate on iOS for better stability
          pitch: isIOSDevice ? 0.9 : 1, // Slightly lower pitch on iOS
        });

        // Only use the interval for non-iOS devices
        if (!isIOSDevice) {
          intervalRef.current = setInterval(() => {
            if (!window.speechSynthesis.speaking) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            } else {
              window.speechSynthesis.pause();
              window.speechSynthesis.resume();
            }
          }, 14000);
        }
      },
      isIOSDevice ? 100 : 0
    ); // Small delay on iOS to ensure previous speech is cancelled
  };

  const value = {
    speak: _speak,
  };
  return <SpeechSynthesisContext.Provider value={value} {...props} />;
};

const useSpeechSynthesis = () => {
  const value = useContext(SpeechSynthesisContext);
  return value;
};

export { SpeechSynthesisProvider, useSpeechSynthesis };
export default SpeechSynthesisContext;
